import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * useFocusHighlight — pairs with `?focus=<field-id>` URL params.
 *
 * The TrustBoostNudge & Investor KYC readiness cards ship deep-link URLs
 * like `/startup/financial-profile?focus=debt`. When the target page
 * mounts, this hook:
 *   1. Waits one animation frame for the DOM to settle.
 *   2. Finds the first element with `data-focus="debt"`.
 *   3. Scrolls it into view.
 *   4. Adds a gold ring pulse for 2.4 seconds.
 *   5. Focuses inputs (if the element is/contains an input/textarea/select).
 *
 * Usage:
 *   // On the target page, once — near the top of the component:
 *   useFocusHighlight();
 *   // Then mark any focusable region:
 *   <div data-focus="debt">…</div>
 *   <input data-focus="funding-ask" />
 */
export default function useFocusHighlight() {
  const { search } = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(search);
    const target = params.get("focus");
    if (!target) return;

    // Give React a paint cycle so lazy fields have mounted.
    const raf = requestAnimationFrame(() => {
      const el = document.querySelector(`[data-focus="${cssEscape(target)}"]`);
      if (!el) return;
      try {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      } catch (_) {
        el.scrollIntoView();
      }
      el.classList.add("vz-focus-pulse");
      const focusable = el.matches("input, textarea, select")
        ? el
        : el.querySelector("input, textarea, select, button, a[href]");
      if (focusable && typeof focusable.focus === "function") {
        // 400ms after scrollIntoView so the browser doesn't fight our smooth scroll.
        setTimeout(() => {
          try { focusable.focus({ preventScroll: true }); } catch (_) { /* ignore */ }
        }, 400);
      }
      setTimeout(() => el.classList.remove("vz-focus-pulse"), 2400);
    });
    return () => cancelAnimationFrame(raf);
  }, [search]);
}

/** Minimal CSS.escape polyfill so target ids can contain special characters. */
function cssEscape(v) {
  if (typeof window !== "undefined" && window.CSS && window.CSS.escape) {
    return window.CSS.escape(v);
  }
  return String(v).replace(/[^a-zA-Z0-9_-]/g, "\\$&");
}
